import { useStaticQuery, graphql } from "gatsby"

import { useMemo } from "react"

const INTERNAL_LOGO_PATH = /settings\/merchant-logos\/internal\//

type RawMerchantLogoNode = {
  uuid: string
  name: string
  full_slug: string
  content: string
}

type MerchantLogosQuery = {
  allStoryblokEntry: {
    nodes: RawMerchantLogoNode[]
  }
}

export type MerchantLogo = {
  uuid: string
  name: string
  fullSlug: string
  content: Storyblok.MerchantLogoConfig
}

const isRenderableMerchantLogo = (
  value: unknown
): value is Storyblok.MerchantLogoConfig =>
  typeof value === "object" &&
  value !== null &&
  "component" in value &&
  value.component === "merchantLogo" &&
  "darkIcon" in value &&
  typeof value.darkIcon === "object" &&
  value.darkIcon !== null &&
  "filename" in value.darkIcon &&
  typeof value.darkIcon.filename === "string" &&
  value.darkIcon.filename.length > 0

export const useMerchantLogos = (useInternalLogos = false): MerchantLogo[] => {
  const data = useStaticQuery<MerchantLogosQuery>(graphql`
    query MerchantLogos {
      allStoryblokEntry(filter: { field_component: { eq: "merchantLogo" } }) {
        nodes {
          uuid
          name
          full_slug
          content
        }
      }
    }
  `)

  return useMemo(() => {
    const logos = data.allStoryblokEntry.nodes.flatMap((node) => {
      let parsedContent: unknown

      try {
        parsedContent = JSON.parse(node.content)
      } catch {
        return []
      }

      if (!isRenderableMerchantLogo(parsedContent)) {
        return []
      }

      if (INTERNAL_LOGO_PATH.test(node.full_slug) !== useInternalLogos) {
        return []
      }

      return [
        {
          uuid: node.uuid,
          name: node.name,
          fullSlug: node.full_slug,
          content: parsedContent,
        },
      ]
    })

    return logos.sort((logo, comparedLogo) =>
      logo.fullSlug.localeCompare(comparedLogo.fullSlug)
    )
  }, [data, useInternalLogos])
}
